/* eslint-disable camelcase */
import {
  useEffect,
  useCallback,
} from 'react';
import {
  Route,
  Routes,
  Navigate,
  useParams,
  useNavigate,
  useLocation,
} from 'react-router';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  getStatus,
} from 'redux-resource';
import i18n from 'i18next';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  uiActions,
  commonActions,
} from 'web-actions';
import {
  usePageView,
} from 'web-hooks';

import {
  Spinner,
  NotFound,
  Dropdown,
  Breadcrumbs,
  ContentWrapper,
  NavigationTabs,
  ProgramTemplate,
  ProgramWorkouts,
  ProgramAllWorkouts,
} from 'web-components';

import {
  Flex,
  PrintLogo,
} from 'web-styled';

import AssignContainer from 'web-containers/AssignContainer';
import * as S from './styles';

const ProgramTemplatesPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const id = Number(params.id);
  const resourceType = 'programTemplates';

  const mapState = useCallback(state => ({
    isPro: coreSelectors.getProfile('is_pro')(state),
    program: coreSelectors.getResourceById(
      resourceType,
      id,
    )(state),
    readStatus: getStatus(
      state,
      [
        `${resourceType}.requests.getProgram.status`,
        'programWeeks.requests.getProgramWeeks.status',
        'programWorkouts.requests.getProgramWorkouts.status',
        `${resourceType}.meta[${id}].readStatus`,
      ],
    ),
    updateStatus: getStatus(
      state,
      [
        `${resourceType}.meta[${id}].updateStatus`,
        'programWeeks.requests.addWeek.status',
        'programWeeks.requests.copyWeek.status',
        'programWorkouts.requests.createProgramWorkout.status',
        'programWorkouts.requests.copyWeekWorkouts.status',
      ],
    ),
    breadcrumbs: coreSelectors.getBreadcrumbs(
      resourceType,
      id,
    )(state),
  }), [id]);

  const {
    isPro,
    program,
    readStatus,
    updateStatus,
    breadcrumbs,
  } = useSelector(mapState, shallowEqual);

  useEffect(() => {
    dispatch(
      coreActions.requestProgram(id, resourceType),
    );
    dispatch(
      coreActions.requestProgramWeeks(id, resourceType),
    );
    dispatch(
      coreActions.requestProgramWorkouts(id, resourceType),
    );
    dispatch(
      coreActions.requestProgramAssignments(id, resourceType),
    );
  }, [id]);

  usePageView({
    eventKey: 'viewed_program_template',
    attrs: program,
    enabled: readStatus.succeeded && program,
  });


  const basePath = '/program_templates';
  const isEdit = (location.pathname || '').includes('/edit');
  const isAll = (location.pathname || '').includes('/all');
  const tabs = [
    {
      name: i18n.t('workouts'),
      to: `${basePath}/${id}/${isAll ? 'all' : 'workouts'}`,
    },
    {
      name: 'Overview',
      to: `${basePath}/${id}/${isEdit ? 'edit' : 'overview'}`,
    },
    {
      name: 'Assign',
      to: `${basePath}/${id}/assign`,
    },
  ];

  const handleUpdate = (payload: any) => {
    dispatch(
      coreActions.updateResources({
        resourceType,
        resources: [{
          id,
          ...payload,
        }],
      }),
    );
    dispatch(
      coreActions.requestUpdateEntity({
        id,
        payload,
        endNotify: true,
        resourceType,
      }),
    );
  };

  const handleDelete = () => {
    const redirect = `${basePath}_folder/${program.folder_id}`;
    const onSuccess = () => {
      navigate(redirect);
    };
    const onConfirmAction = coreActions.requestDeleteEntity({
      id,
      resourceType,
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Delete Program Template?',
          bodyText: `Are you sure you want to delete <b>${program.name}</b>?`,
          helpText: i18n.t('delete.item.trainer.help'),
          confirmText: 'Delete',
        },
        onSuccess,
        onConfirmAction,
      }),
    );
  };

  const actions = [
    {
      name: i18n.t(isAll ? 'program.standard_view' : 'program.all_view'),
      onClick: () => {
        navigate(`${basePath}/${id}/${isAll ? 'workouts' : 'all'}`);
      },
    },
    {
      name: 'Print',
      onClick: () => {
        if (!isAll) {
          navigate(`${basePath}/${id}/all`);
        }
        setTimeout(() => {
          window.print();
          dispatch(commonActions.triggerAnalytic('clicked_print_button'));
        }, 500);
      },
    },
    {
      name: 'Delete',
      color: '#e54d42',
      onClick: handleDelete,
    },
  ];

  return (
    <ContentWrapper
      header={(
        <Flex
          fillSpace
          flex="0 0 50px"
          align="center"
          justify="space-between"
          padding="0px 15px"
          className="header"
        >
          <Breadcrumbs
            items={breadcrumbs}
            basePath={basePath}
            backPath={program?.is_public ? undefined : '/program_templates_folder'}
            entityFolderId={program?.is_public ? undefined : program?.folder_id}
            onChangeLastItem={name => handleUpdate({ name })}
          />
          <Flex noPrint align="center" spacing={10}>
            <NavigationTabs
              tabs={tabs}
            />
            <Dropdown
              trigger={(
                <S.ActionsTrigger>
                  <S.ContextMenu />
                </S.ActionsTrigger>
              )}
              items={actions}
            />
          </Flex>
          <PrintLogo />
        </Flex>
      )}
    >
      {readStatus.succeeded && (
        <Routes>
          <Route
            index
            element={<Navigate to="workouts" />}
          />
          <Route
            path="workouts"
            element={(
              <ProgramWorkouts
                programId={id}
                resourceType={resourceType}
              />
            )}
          />
          <Route
            path="all"
            element={(
              <ProgramAllWorkouts
                programId={id}
                resourceType={resourceType}
              />
            )}
          />
          <Route
            path="overview"
            element={(
              <ProgramTemplate
                readOnly
                isPro={isPro}
                program={program}
                basePath={basePath}
                resourceType={resourceType}
                onRequestDelete={handleDelete}
              />
            )}
          />
          <Route
            path="edit"
            element={(
              <ProgramTemplate
                isPro={isPro}
                program={program}
                basePath={basePath}
                resourceType={resourceType}
                onRequestDelete={handleDelete}
              />
            )}
          />
          <Route
            path="assign"
            element={(
              <AssignContainer
                type="program"
                templateId={id}
              />
            )}
          />
        </Routes>
      )}

      {readStatus.pending && (
        <Spinner message="Loading Program Template..." />
      )}

      {updateStatus.pending && (
        <Spinner over />
      )}

      {readStatus.failed && (
        <NotFound title={`Program Template Not Found (${id})`} />
      )}
    </ContentWrapper>
  );
};

export default ProgramTemplatesPage;
